/**
 * ============================================================================
 * Iron Meridian - Tech Tree Panel
 * ============================================================================
 * 
 * PURPOSE:
 * Displays the tech tree with branching paths, research progress, and 
 * mutual exclusivity visualization.
 * 
 * FEATURES:
 * - 3 branches (Military, Economy, Governance) with 5 tiers each
 * - Visual states: locked, available, researching, completed, excluded
 * - Tooltips showing effects, prerequisites, and exclusions
 * - Research queue selection
 * - Progress bars for active research
 * 
 * ============================================================================
 */

import React, { useState, useMemo, useEffect } from 'react';

// ============================================================================
// TECH TREE DATA (client-side copy for UI)
// ============================================================================

const BRANCHES = {
    MILITARY: { id: 'military', name: 'Military', color: '#EF4444', icon: '⚔️' },
    ECONOMY: { id: 'economy', name: 'Economy', color: '#22C55E', icon: '💰' },
    GOVERNANCE: { id: 'governance', name: 'Governance', color: '#3B82F6', icon: '🏛️' }
};

// Simplified tech definitions for client (full data from server)
const TECH_DATA = {
    // ═══ MILITARY BRANCH ═══
    basic_training: { id: 'basic_training', name: 'Basic Training', branch: 'military', tier: 1, cost: 100, prerequisites: [], exclusions: [], effects: [{ type: 'COMBAT_STRENGTH', value: 0.05 }] },
    fortifications: { id: 'fortifications', name: 'Fortifications', branch: 'military', tier: 1, cost: 100, prerequisites: [], exclusions: [], effects: [{ type: 'DEFENSE_BONUS', value: 0.10 }] },
    
    professional_army: { id: 'professional_army', name: 'Professional Army', branch: 'military', tier: 2, cost: 200, prerequisites: ['basic_training'], exclusions: ['citizen_militia'], effects: [{ type: 'COMBAT_STRENGTH', value: 0.15 }, { type: 'MAINTENANCE_COST', value: 0.20 }] },
    citizen_militia: { id: 'citizen_militia', name: 'Citizen Militia', branch: 'military', tier: 2, cost: 150, prerequisites: ['basic_training'], exclusions: ['professional_army'], effects: [{ type: 'COMBAT_STRENGTH', value: 0.05 }, { type: 'DEFENSE_BONUS', value: 0.15 }] },
    siege_warfare: { id: 'siege_warfare', name: 'Siege Warfare', branch: 'military', tier: 2, cost: 180, prerequisites: ['fortifications'], exclusions: [], effects: [{ type: 'SIEGE_BONUS', value: 0.25 }] },
    
    modern_tactics: { id: 'modern_tactics', name: 'Modern Tactics', branch: 'military', tier: 3, cost: 350, prerequisites: ['professional_army'], exclusions: [], effects: [{ type: 'COMBAT_STRENGTH', value: 0.20 }, { type: 'FIRST_STRIKE', value: 0.10 }] },
    guerrilla_warfare: { id: 'guerrilla_warfare', name: 'Guerrilla Warfare', branch: 'military', tier: 3, cost: 300, prerequisites: ['citizen_militia'], exclusions: [], effects: [{ type: 'DEFENSE_BONUS', value: 0.25 }, { type: 'ATTRITION_DAMAGE', value: 0.15 }] },
    
    blitzkrieg: { id: 'blitzkrieg', name: 'Blitzkrieg', branch: 'military', tier: 4, cost: 500, prerequisites: ['modern_tactics'], exclusions: ['defense_in_depth'], effects: [{ type: 'COMBAT_STRENGTH', value: 0.30 }, { type: 'MOVEMENT_BONUS', value: 1 }] },
    defense_in_depth: { id: 'defense_in_depth', name: 'Defense in Depth', branch: 'military', tier: 4, cost: 500, prerequisites: ['modern_tactics', 'fortifications'], exclusions: ['blitzkrieg'], effects: [{ type: 'DEFENSE_BONUS', value: 0.40 }, { type: 'ATTRITION_DAMAGE', value: 0.20 }] },
    
    nuclear_weapons: { id: 'nuclear_weapons', name: 'Nuclear Weapons', branch: 'military', tier: 5, cost: 1000, prerequisites: ['blitzkrieg'], exclusions: [], effects: [{ type: 'DEVASTATION_ABILITY', value: 1 }, { type: 'DIPLOMATIC_PENALTY', value: -30 }] },
    fortress_nation: { id: 'fortress_nation', name: 'Fortress Nation', branch: 'military', tier: 5, cost: 800, prerequisites: ['defense_in_depth', 'guerrilla_warfare'], exclusions: [], effects: [{ type: 'DEFENSE_BONUS', value: 0.50 }, { type: 'INVASION_IMMUNITY_TURNS', value: 2 }] },
    
    // ═══ ECONOMY BRANCH ═══
    agriculture: { id: 'agriculture', name: 'Agriculture', branch: 'economy', tier: 1, cost: 100, prerequisites: [], exclusions: [], effects: [{ type: 'INCOME_MULTIPLIER', value: 0.10 }] },
    basic_industry: { id: 'basic_industry', name: 'Basic Industry', branch: 'economy', tier: 1, cost: 100, prerequisites: [], exclusions: [], effects: [{ type: 'PRODUCTION_BONUS', value: 0.10 }] },
    
    trade_networks: { id: 'trade_networks', name: 'Trade Networks', branch: 'economy', tier: 2, cost: 200, prerequisites: ['agriculture'], exclusions: ['self_sufficiency'], effects: [{ type: 'INCOME_MULTIPLIER', value: 0.15 }, { type: 'NEIGHBOR_TRADE_BONUS', value: 0.05 }] },
    self_sufficiency: { id: 'self_sufficiency', name: 'Self-Sufficiency', branch: 'economy', tier: 2, cost: 180, prerequisites: ['agriculture'], exclusions: ['trade_networks'], effects: [{ type: 'INCOME_MULTIPLIER', value: 0.08 }, { type: 'BLOCKADE_RESISTANCE', value: 0.50 }] },
    
    banking_system: { id: 'banking_system', name: 'Banking System', branch: 'economy', tier: 3, cost: 350, prerequisites: ['trade_networks'], exclusions: [], effects: [{ type: 'INCOME_MULTIPLIER', value: 0.20 }, { type: 'LOAN_ACCESS', value: 1 }] },
    industrialization: { id: 'industrialization', name: 'Industrialization', branch: 'economy', tier: 3, cost: 400, prerequisites: ['basic_industry'], exclusions: [], effects: [{ type: 'PRODUCTION_BONUS', value: 0.25 }, { type: 'POLLUTION', value: 0.10 }] },
    
    global_trade: { id: 'global_trade', name: 'Global Trade', branch: 'economy', tier: 4, cost: 500, prerequisites: ['banking_system'], exclusions: [], effects: [{ type: 'INCOME_MULTIPLIER', value: 0.30 }, { type: 'SEA_TRADE_ROUTES', value: 1 }] },
    automation: { id: 'automation', name: 'Automation', branch: 'economy', tier: 4, cost: 550, prerequisites: ['industrialization'], exclusions: [], effects: [{ type: 'PRODUCTION_BONUS', value: 0.40 }, { type: 'UNEMPLOYMENT', value: 0.15 }] },
    
    economic_hegemony: { id: 'economic_hegemony', name: 'Economic Hegemony', branch: 'economy', tier: 5, cost: 1000, prerequisites: ['global_trade', 'automation'], exclusions: [], effects: [{ type: 'INCOME_MULTIPLIER', value: 0.50 }, { type: 'MARKET_MANIPULATION', value: 1 }] },
    war_economy: { id: 'war_economy', name: 'War Economy', branch: 'economy', tier: 5, cost: 800, prerequisites: ['automation'], exclusions: [], effects: [{ type: 'MILITARY_PRODUCTION', value: 0.40 }, { type: 'CIVILIAN_ECONOMY', value: -0.20 }] },
    
    // ═══ GOVERNANCE BRANCH ═══
    civil_service: { id: 'civil_service', name: 'Civil Service', branch: 'governance', tier: 1, cost: 100, prerequisites: [], exclusions: [], effects: [{ type: 'STABILITY_RECOVERY', value: 0.05 }] },
    public_order: { id: 'public_order', name: 'Public Order', branch: 'governance', tier: 1, cost: 100, prerequisites: [], exclusions: [], effects: [{ type: 'UNREST_REDUCTION', value: 0.10 }] },
    
    constitutional_law: { id: 'constitutional_law', name: 'Constitutional Law', branch: 'governance', tier: 2, cost: 200, prerequisites: ['civil_service'], exclusions: ['centralized_authority'], effects: [{ type: 'STABILITY_RECOVERY', value: 0.10 }, { type: 'REFORM_COST', value: -0.20 }] },
    centralized_authority: { id: 'centralized_authority', name: 'Centralized Authority', branch: 'governance', tier: 2, cost: 180, prerequisites: ['civil_service'], exclusions: ['constitutional_law'], effects: [{ type: 'STABILITY_RECOVERY', value: 0.05 }, { type: 'DECREE_POWER', value: 1 }] },
    
    representative_government: { id: 'representative_government', name: 'Representative Government', branch: 'governance', tier: 3, cost: 350, prerequisites: ['constitutional_law'], exclusions: ['totalitarian_state'], effects: [{ type: 'STABILITY_RECOVERY', value: 0.15 }, { type: 'INCOME_MULTIPLIER', value: 0.10 }] },
    totalitarian_state: { id: 'totalitarian_state', name: 'Totalitarian State', branch: 'governance', tier: 3, cost: 300, prerequisites: ['centralized_authority'], exclusions: ['representative_government'], effects: [{ type: 'MILITARY_BONUS', value: 0.15 }, { type: 'STABILITY_PENALTY', value: -0.10 }] },
    
    universal_suffrage: { id: 'universal_suffrage', name: 'Universal Suffrage', branch: 'governance', tier: 4, cost: 450, prerequisites: ['representative_government'], exclusions: [], effects: [{ type: 'STABILITY_RECOVERY', value: 0.20 }, { type: 'PRODUCTION_BONUS', value: 0.10 }] },
    secret_police: { id: 'secret_police', name: 'Secret Police', branch: 'governance', tier: 4, cost: 400, prerequisites: ['totalitarian_state'], exclusions: [], effects: [{ type: 'UNREST_REDUCTION', value: 0.30 }, { type: 'ESPIONAGE_BONUS', value: 0.25 }] },
    
    global_influence: { id: 'global_influence', name: 'Global Influence', branch: 'governance', tier: 5, cost: 900, prerequisites: ['universal_suffrage'], exclusions: [], effects: [{ type: 'DIPLOMATIC_BONUS', value: 0.40 }, { type: 'ALLIANCE_CAPACITY', value: 2 }] },
    surveillance_state: { id: 'surveillance_state', name: 'Surveillance State', branch: 'governance', tier: 5, cost: 850, prerequisites: ['secret_police'], exclusions: [], effects: [{ type: 'UNREST_REDUCTION', value: 0.50 }, { type: 'REVOLT_PREVENTION', value: 1 }] }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getTechState(techId, researchState) {
    const { researched = [], currentResearch, researchPoints = 0 } = researchState || {};
    const tech = TECH_DATA[techId];
    
    if (!tech) return 'unknown';
    
    // Completed
    if (researched.includes(techId)) {
        return 'completed';
    }
    
    // Currently researching - handle both string and object formats
    const currentTechId = typeof currentResearch === 'object' ? currentResearch?.techId : currentResearch;
    if (currentTechId === techId) {
        return 'researching';
    }
    
    // Check if excluded by a completed tech
    for (const exclusion of tech.exclusions) {
        if (researched.includes(exclusion)) {
            return 'excluded';
        }
    }
    
    // Check prerequisites
    const hasPrereqs = tech.prerequisites.every(prereq => researched.includes(prereq));
    
    if (!hasPrereqs) {
        return 'locked';
    }
    
    return 'available';
}

function getResearchProgress(researchState) {
    const { currentResearch, researchPoints = 0 } = researchState || {};
    if (!currentResearch) return null;
    
    // Handle currentResearch as object or string
    const techId = typeof currentResearch === 'object' ? currentResearch?.techId : currentResearch;
    const tech = TECH_DATA[techId];
    if (!tech) return null;
    
    // Get accumulated points from currentResearch object or use researchPoints
    const accumulated = typeof currentResearch === 'object' 
        ? (currentResearch.pointsAccumulated || 0) 
        : researchPoints;
    
    return {
        techId: techId,
        current: accumulated,
        required: tech.cost,
        percentage: Math.min(100, Math.floor((accumulated / tech.cost) * 100))
    };
}

function getEffectDescription(effect) {
    const value = effect.value > 0 ? `+${(effect.value * 100).toFixed(0)}%` : `${(effect.value * 100).toFixed(0)}%`;
    
    switch (effect.type) {
        case 'COMBAT_STRENGTH': return `${value} Combat Strength`;
        case 'DEFENSE_BONUS': return `${value} Defense`;
        case 'INCOME_MULTIPLIER': return `${value} Income`;
        case 'PRODUCTION_BONUS': return `${value} Production`;
        case 'STABILITY_RECOVERY': return `${value} Stability Recovery`;
        case 'UNREST_REDUCTION': return `${value} Unrest Reduction`;
        case 'MAINTENANCE_COST': return `${value} Maintenance Cost`;
        case 'SIEGE_BONUS': return `${value} Siege Bonus`;
        case 'FIRST_STRIKE': return `${value} First Strike Chance`;
        case 'ATTRITION_DAMAGE': return `${value} Attrition Damage`;
        case 'MOVEMENT_BONUS': return `+${effect.value} Movement`;
        case 'DIPLOMATIC_BONUS': return `${value} Diplomatic Influence`;
        case 'DIPLOMATIC_PENALTY': return `${effect.value} Diplomatic Standing`;
        case 'ESPIONAGE_BONUS': return `${value} Espionage`;
        case 'MILITARY_BONUS': return `${value} Military Strength`;
        case 'STABILITY_PENALTY': return `${(effect.value * 100).toFixed(0)}% Stability`;
        default: return `${effect.type}: ${effect.value}`;
    }
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function TechNode({ tech, state, onSelect, isSelected }) {
    const stateStyles = {
        completed: 'bg-green-600/30 border-green-500 text-green-100',
        researching: 'bg-amber-600/30 border-amber-500 text-amber-100 animate-pulse',
        available: 'bg-slate-700 border-slate-500 text-slate-100 hover:border-sky-400 cursor-pointer',
        locked: 'bg-slate-800/50 border-slate-700 text-slate-500',
        excluded: 'bg-red-900/20 border-red-800/50 text-red-400/50 line-through'
    };
    
    const stateIcons = {
        completed: '✓',
        researching: '⏳',
        available: '○',
        locked: '🔒',
        excluded: '✗'
    };
    
    return (
        <div
            className={`
                relative px-3 py-2 rounded-lg border-2 transition-all
                ${stateStyles[state] || stateStyles.locked}
                ${isSelected ? 'ring-2 ring-sky-400' : ''}
            `}
            onClick={() => {
                console.log('TechNode clicked:', tech.id, 'state:', state)
                if (state === 'available') {
                    onSelect(tech.id)
                }
            }}
        >
            <div className="flex items-center gap-2">
                <span className="text-xs">{stateIcons[state]}</span>
                <span className="text-sm font-medium truncate">{tech.name}</span>
            </div>
            <div className="text-xs text-slate-400 mt-1">
                Cost: {tech.cost}
            </div>
        </div>
    );
}

function TechTooltip({ tech, state, researchState }) {
    if (!tech) return null;
    
    const progress = state === 'researching' ? getResearchProgress(researchState) : null;
    
    return (
        <div className="absolute z-50 w-72 p-4 bg-slate-900 border border-slate-600 rounded-lg shadow-xl">
            <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">{BRANCHES[tech.branch.toUpperCase()]?.icon || '📚'}</span>
                <h3 className="font-bold text-white">{tech.name}</h3>
            </div>
            
            <div className="text-sm text-slate-400 mb-3">
                Tier {tech.tier} • {tech.cost} Research Points
            </div>
            
            {progress && (
                <div className="mb-3">
                    <div className="flex justify-between text-xs text-slate-400 mb-1">
                        <span>Research Progress</span>
                        <span>{progress.current}/{progress.required}</span>
                    </div>
                    <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div 
                            className="h-full bg-amber-500 transition-all"
                            style={{ width: `${progress.percentage}%` }}
                        />
                    </div>
                </div>
            )}
            
            <div className="mb-3">
                <h4 className="text-xs font-semibold text-slate-300 uppercase mb-1">Effects</h4>
                <ul className="space-y-1">
                    {tech.effects.map((effect, i) => (
                        <li key={i} className={`text-sm ${effect.value >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {getEffectDescription(effect)}
                        </li>
                    ))}
                </ul>
            </div>
            
            {tech.prerequisites.length > 0 && (
                <div className="mb-2">
                    <h4 className="text-xs font-semibold text-slate-300 uppercase mb-1">Prerequisites</h4>
                    <div className="flex flex-wrap gap-1">
                        {tech.prerequisites.map(prereq => (
                            <span key={prereq} className="px-2 py-0.5 text-xs bg-slate-700 rounded">
                                {TECH_DATA[prereq]?.name || prereq}
                            </span>
                        ))}
                    </div>
                </div>
            )}
            
            {tech.exclusions.length > 0 && (
                <div>
                    <h4 className="text-xs font-semibold text-red-400 uppercase mb-1">Excludes</h4>
                    <div className="flex flex-wrap gap-1">
                        {tech.exclusions.map(excl => (
                            <span key={excl} className="px-2 py-0.5 text-xs bg-red-900/30 text-red-300 rounded">
                                {TECH_DATA[excl]?.name || excl}
                            </span>
                        ))}
                    </div>
                </div>
            )}
            
            {state === 'excluded' && (
                <div className="mt-2 text-xs text-red-400">
                    ⚠️ Blocked by previous research choice
                </div>
            )}
            
            {state === 'available' && (
                <div className="mt-2 text-xs text-sky-400">
                    Click to begin research
                </div>
            )}
        </div>
    );
}

function BranchColumn({ branch, techs, researchState, onSelectTech, selectedTech, hoveredTech, onHover }) {
    const tiers = [1, 2, 3, 4, 5];
    
    return (
        <div className="flex-1 min-w-[200px]">
            {/* Branch Header */}
            <div 
                className="text-center py-2 mb-4 rounded-t-lg"
                style={{ backgroundColor: `${branch.color}20`, borderBottom: `2px solid ${branch.color}` }}
            >
                <span className="text-xl">{branch.icon}</span>
                <h3 className="font-bold text-white">{branch.name}</h3>
            </div>
            
            {/* Tech Tiers */}
            <div className="space-y-3">
                {tiers.map(tier => {
                    const tierTechs = techs.filter(t => t.tier === tier);
                    
                    return (
                        <div key={tier} className="min-h-[60px]">
                            <div className="space-y-2">
                                {tierTechs.map(tech => {
                                    const state = getTechState(tech.id, researchState);
                                    const isHovered = hoveredTech === tech.id;
                                    const isSelected = selectedTech === tech.id;
                                    
                                    return (
                                        <div 
                                            key={tech.id}
                                            className="relative"
                                            onMouseEnter={() => onHover(tech.id)}
                                            onMouseLeave={() => onHover(null)}
                                        >
                                            <TechNode
                                                tech={tech}
                                                state={state}
                                                onSelect={onSelectTech}
                                                isSelected={isSelected}
                                            />
                                            {isHovered && (
                                                <TechTooltip 
                                                    tech={tech}
                                                    state={state}
                                                    researchState={researchState}
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function TechTreePanel({ 
    researchState = {}, 
    onStartResearch, 
    onCancelResearch,
    isOpen = true,
    onClose,
    playerEconomy = 0
}) {
    const [hoveredTech, setHoveredTech] = useState(null);
    const [selectedTech, setSelectedTech] = useState(null);
    const [showResearchTooltip, setShowResearchTooltip] = useState(false);
    
    // Calculate research points per turn
    const baseResearchPoints = 10;
    const economyBonus = Math.floor(playerEconomy / 10);
    const totalPointsPerTurn = baseResearchPoints + economyBonus;
    
    // Debug: log researchState when it changes
    useEffect(() => {
        console.log('TechTreePanel received researchState:', researchState);
        console.log('currentResearch:', researchState?.currentResearch);
    }, [researchState]);
    
    // Organize techs by branch
    const techsByBranch = useMemo(() => {
        return Object.values(TECH_DATA).reduce((acc, tech) => {
            if (!acc[tech.branch]) acc[tech.branch] = [];
            acc[tech.branch].push(tech);
            return acc;
        }, {});
    }, []);
    
    const currentProgress = getResearchProgress(researchState);
    
    const handleSelectTech = (techId) => {
        // Directly start research on single click
        if (onStartResearch) {
            onStartResearch(techId);
        }
    };
    
    if (!isOpen) return null;
    
    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60">
            <div className="w-full max-w-5xl max-h-[90vh] m-4 bg-slate-800 rounded-xl shadow-2xl overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 bg-slate-900 border-b border-slate-700">
                    <div>
                        <h2 className="text-xl font-bold text-white">Technology Research</h2>
                        <p className="text-sm text-slate-400">
                            Choose your path wisely - some technologies are mutually exclusive
                        </p>
                    </div>
                    <div className="flex items-center gap-4">
                        {/* Research Points Display with Tooltip */}
                        <div 
                            className="relative"
                            onMouseEnter={() => setShowResearchTooltip(true)}
                            onMouseLeave={() => setShowResearchTooltip(false)}
                        >
                            <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-600/30 border border-amber-500/50 rounded-lg cursor-help">
                                <span className="text-amber-400">🔬</span>
                                <span className="text-amber-300 font-medium">+{totalPointsPerTurn}/turn</span>
                            </div>
                            {/* Tooltip */}
                            {showResearchTooltip && (
                                <div className="absolute right-0 top-full mt-2 w-64 p-3 bg-slate-900 border border-slate-600 rounded-lg shadow-xl z-50">
                                    <h4 className="text-sm font-bold text-white mb-2">Research Points per Turn</h4>
                                    <div className="space-y-1 text-xs">
                                        <div className="flex justify-between">
                                            <span className="text-slate-400">Base research:</span>
                                            <span className="text-white">+{baseResearchPoints}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-slate-400">Economy bonus ({playerEconomy} ÷ 10):</span>
                                            <span className="text-green-400">+{economyBonus}</span>
                                        </div>
                                        <div className="border-t border-slate-700 pt-1 mt-1 flex justify-between font-bold">
                                            <span className="text-slate-300">Total:</span>
                                            <span className="text-amber-400">+{totalPointsPerTurn}/turn</span>
                                        </div>
                                    </div>
                                    <p className="text-xs text-slate-500 mt-2 italic">
                                        💡 Grow your economy to research faster!
                                    </p>
                                </div>
                            )}
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 text-slate-400 hover:text-white transition"
                        >
                            ✕
                        </button>
                    </div>
                </div>
                
                {/* Current Research Status */}
                {currentProgress && (
                    <div className="px-6 py-3 bg-amber-900/30 border-b border-amber-700/50">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <span className="text-amber-400">⏳</span>
                                <span className="text-white">
                                    Researching: <strong>{TECH_DATA[currentProgress.techId]?.name}</strong>
                                </span>
                            </div>
                            <div className="flex items-center gap-4">
                                <div className="w-48">
                                    <div className="flex justify-between text-xs text-amber-300 mb-1">
                                        <span>Progress</span>
                                        <span>{currentProgress.percentage}%</span>
                                    </div>
                                    <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
                                        <div 
                                            className="h-full bg-amber-500 transition-all"
                                            style={{ width: `${currentProgress.percentage}%` }}
                                        />
                                    </div>
                                </div>
                                {onCancelResearch && (
                                    <button
                                        onClick={onCancelResearch}
                                        className="px-3 py-1 text-sm bg-red-600/50 hover:bg-red-600 rounded transition"
                                    >
                                        Cancel
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
                
                {/* Tech Tree Grid */}
                <div className="flex-1 overflow-auto p-6">
                    <div className="flex gap-4 min-w-max">
                        {/* Tier Labels Column */}
                        <div className="w-12 flex-shrink-0">
                            <div className="h-[72px] mb-4"></div> {/* Spacer for branch headers */}
                            <div className="space-y-3">
                                {[1, 2, 3, 4, 5].map(tier => (
                                    <div key={tier} className="min-h-[60px] flex items-center justify-center">
                                        <span className="px-2 py-1 text-xs font-bold text-slate-400 bg-slate-700/50 rounded">
                                            Tier {tier}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        {Object.values(BRANCHES).map(branch => (
                            <BranchColumn
                                key={branch.id}
                                branch={branch}
                                techs={techsByBranch[branch.id] || []}
                                researchState={researchState}
                                onSelectTech={handleSelectTech}
                                selectedTech={selectedTech}
                                hoveredTech={hoveredTech}
                                onHover={setHoveredTech}
                            />
                        ))}
                    </div>
                </div>
                
                {/* Footer with Legend */}
                <div className="px-6 py-3 bg-slate-900 border-t border-slate-700">
                    <div className="flex flex-wrap gap-4 text-xs">
                        <div className="flex items-center gap-1">
                            <span className="w-3 h-3 rounded bg-green-600"></span>
                            <span className="text-slate-400">Completed</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <span className="w-3 h-3 rounded bg-amber-600 animate-pulse"></span>
                            <span className="text-slate-400">Researching</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <span className="w-3 h-3 rounded bg-slate-600 border border-slate-400"></span>
                            <span className="text-slate-400">Available</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <span className="w-3 h-3 rounded bg-slate-800 border border-slate-700"></span>
                            <span className="text-slate-400">Locked</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <span className="w-3 h-3 rounded bg-red-900/50 border border-red-800/50"></span>
                            <span className="text-slate-400">Excluded</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
